import React, { useState } from 'react';
import { Table, Button, Space, Tag, Empty, Spin, message, Tabs } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { menteeProfileQueryKeys } from '../../../lib/queryClient';
import PanelTabCard from '../../../components/Common/PanelTabCard';

interface IncomingFavouriteRequest {
  id: number;
  mentorName: string;
  mentorId: number;
  createdAt: string;
  message?: string;
  type: number;
  applicationId: number;
}

interface ReferrerMentor {
  mentorId: number;
  mentorName: string;
  createdAt: string;
}

const ReferanslarTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('incoming');

  const { data: list = [], isLoading, refetch } = useQuery({
    queryKey: menteeProfileQueryKeys.incomingFavouriteRequests,
    queryFn: async () => {
      const res = await apiService.getIncomingFavouriteRequests();
      if (!res.success) throw new Error(res.message);
      return res.data ?? [];
    },
  });

  const { data: referrers = [], isLoading: loadingReferrers, refetch: refetchReferrers } = useQuery({
    queryKey: menteeProfileQueryKeys.referrerMentors,
    queryFn: async () => {
      const res = await apiService.getReferrerMentors();
      if (!res.success) throw new Error(res.message);
      return res.data ?? [];
    },
  });

  const acceptMutation = useMutation({
    mutationFn: (id: number) => apiService.acceptFavouriteRequest(id),
    onSuccess: (res) => {
      if (res.success) {
        message.success('Referans talebi kabul edildi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.incomingFavouriteRequests });
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.referrerMentors });
      } else message.error(res.message || 'İşlem yapılamadı.');
    },
    onError: () => message.error('Bir hata oluştu.'),
  });

  const rejectMutation = useMutation({
    mutationFn: (id: number) => apiService.rejectFavouriteRequest(id),
    onSuccess: (res) => {
      if (res.success) {
        message.success('Referans talebi reddedildi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.incomingFavouriteRequests });
      } else message.error(res.message || 'İşlem yapılamadı.');
    },
    onError: () => message.error('Bir hata oluştu.'),
  });

  const incomingColumns = [
    { title: 'Mentor', dataIndex: 'mentorName', key: 'mentorName', render: (t: string) => <strong>{t}</strong> },
    {
      title: 'Tip',
      dataIndex: 'type',
      key: 'type',
      render: (type: number) => <Tag color={type === 0 ? 'blue' : 'green'}>{type === 0 ? '1-1 Görüşme' : 'Bootcamp'}</Tag>,
    },
    { title: 'Mesaj', dataIndex: 'message', key: 'message', ellipsis: true, render: (m: string) => m || '—' },
    { title: 'Tarih', dataIndex: 'createdAt', key: 'createdAt', render: (d: string) => new Date(d).toLocaleDateString('tr-TR') },
    {
      title: 'İşlem',
      key: 'action',
      render: (_: unknown, record: IncomingFavouriteRequest) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => acceptMutation.mutate(record.id)}
            loading={acceptMutation.isPending}
          >
            Kabul et
          </Button>
          <Button
            size="small"
            danger
            icon={<CloseCircleOutlined />}
            onClick={() => rejectMutation.mutate(record.id)}
            loading={rejectMutation.isPending}
          >
            Reddet
          </Button>
        </Space>
      ),
    },
  ];

  const referrerColumns = [
    { title: 'Mentor', dataIndex: 'mentorName', key: 'mentorName', render: (t: string) => <strong>{t}</strong> },
    { title: 'Eklenme tarihi', dataIndex: 'createdAt', key: 'createdAt', render: (d: string) => new Date(d).toLocaleDateString('tr-TR') },
    {
      title: 'İşlem',
      key: 'action',
      render: (_: unknown, record: ReferrerMentor) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => window.open(`/mentor/${record.mentorId}`, '_blank')}
        >
          Profili görüntüle
        </Button>
      ),
    },
  ];

  return (
    <PanelTabCard
      title="Referanslar"
      description="Referans taleplerini yönetin ve size referans veren mentörleri görüntüleyin."
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Gelen referans talepleri" key="incoming">
          <div style={{ marginBottom: 16 }}>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
              Yenile
            </Button>
          </div>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
          ) : list.length === 0 ? (
            <Empty
              description="Bekleyen referans talebiniz yok"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                Mentorların sizi referans (favori) olarak işaretlemek için gönderdiği talepler burada görünür. Kabul ederseniz profilinizde referans veren mentorlar listelenir.
              </span>
            </Empty>
          ) : (
            <Table rowKey="id" columns={incomingColumns} dataSource={list} pagination={{ pageSize: 10 }} />
          )}
        </Tabs.TabPane>
        <Tabs.TabPane tab="Referans veren mentörler" key="referrers">
          <div style={{ marginBottom: 16 }}>
            <Button icon={<ReloadOutlined />} onClick={() => refetchReferrers()} loading={loadingReferrers}>
              Yenile
            </Button>
          </div>
          {loadingReferrers ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
          ) : referrers.length === 0 ? (
            <Empty
              description="Henüz referans veren mentörünüz yok"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                Referans talebini kabul ettiğiniz mentörler burada listelenir. Profilinizde referans veren mentörler bölümünde herkese açık görünür.
              </span>
            </Empty>
          ) : (
            <Table rowKey="mentorId" columns={referrerColumns} dataSource={referrers} pagination={{ pageSize: 10 }} />
          )}
        </Tabs.TabPane>
      </Tabs>
    </PanelTabCard>
  );
};

export default ReferanslarTab;
